import {
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react'

import debounce from 'lodash/debounce'

export interface ElementSize {
    width: number
    height: number
}

const useElementSize = <T extends HTMLElement = HTMLElement>(
    ref: RefObject<T | null> | MutableRefObject<Element | null>
): ElementSize => {
    const [size, setSize] = useState<ElementSize>({
        width: 0,
        height: 0,
    })

    const measureElement = useCallback(() => {
        const element = ref?.current
        if (element) {
            const boundingRect = element.getBoundingClientRect()
            setSize({
                width: boundingRect.width,
                height: boundingRect.height,
            })
        }
    }, [ref])

    useEffect(() => {
        const element = ref?.current
        if (!element) {
            return
        }

        const handleResize = debounce(measureElement, 50)

        const observer = new ResizeObserver(handleResize)
        observer.observe(element)

        return () => {
            if (element) {
                observer.unobserve(element)
            } else {
                observer.disconnect()
            }
        }
    }, [ref, measureElement])

    // Initial measurement
    useLayoutEffect(() => {
        requestAnimationFrame(() => {
            measureElement()
        })
    }, [measureElement])

    return size
}

export default useElementSize
